import { Routes } from '@angular/router';
import { AuthenticationGuard, AuthorizationGuard } from '@softline/auth';
import { importProvidersFrom } from '@angular/core';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SOFTLINE_STORE_VALIDATION_SERVICE,
  SoftlineEntityService,
  SoftlineValidationService,
  StoreModule,
  ValidationStore,
} from '@softline/core';
import {
  SOFTLINE_FEATURE_TICKET,
  SOFTLINE_FEATURE_TICKET_CHECKPOINT,
  SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
  SOFTLINE_FEATURE_TICKET_COMMENT,
  SOFTLINE_FEATURE_TICKET_CONTACT,
  SOFTLINE_FEATURE_TICKET_DOCUMENT,
  SOFTLINE_FEATURE_TICKET_FILTER,
  SOFTLINE_FEATURE_TICKET_HOTLINE,
  SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
  SOFTLINE_FEATURE_TICKET_TIME_REPORT,
  SOFTLINE_PERMISSION_TICKET,
  TICKET_ORDER_CONFIRMATION_STEPS,
  TICKET_SCHEDULE_CONFIRMATION_STEPS,
  TICKET_TEST_CONFIRMATION_STEPS,
  TicketCheckpointStateStore,
  TicketCheckpointStore,
  TicketCommentStore,
  TicketContactStore,
  TicketFilterStore,
  TicketHotlineStore,
  TicketTimeReportStore,
  TicketTypeStore,
} from './ticket.shared';
import {
  SOFTLINE_API_CHECKPOINT,
  SOFTLINE_API_CHECKPOINT_STATE,
  SOFTLINE_API_HOTLINE,
  SOFTLINE_API_TICKET_COMMENT,
  SOFTLINE_API_TICKET_CONTACT,
  SOFTLINE_API_TICKET_DOCUMENT,
  SOFTLINE_API_TICKET_FILTER,
  SOFTLINE_API_TICKET_TIME_REPORT,
  SOFTLINE_API_TICKET_TYPE,
  SOFTLINE_API_TICKET_VALIDATE,
} from './ticket.api';
import { TicketDocumentsStore } from './store/document.store';
import { UploadTicketDocumentService } from './services/upload-ticket-document.service';
import { ticketFiltersResolver } from './resolvers/ticket-filter.resolver';
import { createTicketDefaultValuesResolver } from './resolvers/create-ticket-default-values.resolver';

export const ticketRoutes: Routes = [
  {
    path: 'ticket',
    resolve: {
      ticketFilters: ticketFiltersResolver,
    },
    loadComponent: () =>
      import('./pages/ticket-app.component').then((o) => o.TicketAppComponent),
    children: [
      {
        path: 'create',
        resolve: {
          defaultFormValues: createTicketDefaultValuesResolver,
        },
        loadComponent: () =>
          import('./pages/create-ticket/create-ticket.component').then(
            (o) => o.CreateTicketComponent
          ),
      },
      {
        path: 'search',
        loadComponent: () =>
          import('./pages/ticket-search/ticket-search.component').then(
            (o) => o.TicketSearchComponent
          ),
      },
      {
        path: ':id',
        loadComponent: () => import('./pages/ticket-detail/ticket-detail'),
      },
      {
        path: '',
        loadComponent: () =>
          import('./pages/ticket-overview/ticket-overview.component').then(
            (o) => o.TicketOverviewComponent
          ),
      },
      { path: '**', redirectTo: '/ticket' },
    ],
    canActivate: [AuthenticationGuard, AuthorizationGuard],
    data: { permission: SOFTLINE_PERMISSION_TICKET },
    providers: [
      importProvidersFrom(
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_CHECKPOINT,
          feature: TicketCheckpointStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_CHECKPOINT),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
          feature: TicketCheckpointStateStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_CHECKPOINT_STATE),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_COMMENT,
          feature: TicketCommentStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_TICKET_COMMENT),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_CONTACT,
          feature: TicketContactStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_TICKET_CONTACT),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_DOCUMENT,
          feature: TicketDocumentsStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_TICKET_DOCUMENT),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
            {
              provide: UploadTicketDocumentService,
              useFactory: (service: ConnectionHttpService) =>
                new UploadTicketDocumentService(service),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_FILTER,
          feature: TicketFilterStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_TICKET_FILTER),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_HOTLINE,
          feature: TicketHotlineStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_HOTLINE),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET,
          feature: ValidationStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_VALIDATION_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineValidationService(service, SOFTLINE_API_TICKET_VALIDATE),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
          feature: TicketTypeStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_TICKET_TYPE),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        }),
        StoreModule.forFeature({
          name: SOFTLINE_FEATURE_TICKET_TIME_REPORT,
          feature: TicketTimeReportStore.feature,
          providers: [
            {
              provide: SOFTLINE_STORE_ENTITY_SERVICE,
              useFactory: (service: ConnectionHttpService) =>
                new SoftlineEntityService(service, SOFTLINE_API_TICKET_TIME_REPORT),
              deps: [SOFTLINE_SERVICE_HTTP],
            },
          ],
        })
      ),
      // The step lists only select the introductory text of the confirmation block.
      // Whether the block (and its buttons) is shown is decided by the backend via
      // ticket.nextSteps (TicketLogic.BestaetigungTyp); the names are the task names
      // of the ticket workflow.
      {
        provide: TICKET_ORDER_CONFIRMATION_STEPS,
        useValue: [
          'Kundenbestätigung einholen',
          'Kundenbestätigung einholen (Wunsch)',
          'Kundenbestätigung einholen (Regie)',
          'Kundenbestätigung einholen (Technik Wunsch)',
          'Kundenbestätigung einholen (Technik Regie)',
        ],
      },
      {
        provide: TICKET_TEST_CONFIRMATION_STEPS,
        useValue: [
          'Kunden-Testbestätigung abwarten',
          'Kunden-Testbestätigung abwarten (Entwicklung)',
          'Kunden-Testbestätigung abwarten (Technik)',
          'Kunden-Testsystem-Testbestätigung abwarten',
          'Testsystem-Testbestätigung abwarten',
          'Echtsystem-Testbestätigung abwarten',
        ],
      },
      {
        provide: TICKET_SCHEDULE_CONFIRMATION_STEPS,
        useValue: ['Termine bestätigen', 'Release: Termine bestätigen'],
      },
    ],
  },
];
